import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Space, Group, Title, Grid, Tabs, Button } from '@mantine/core';
import { BsPlusCircle } from 'react-icons/bs';
import KYCProducts from './kyc_products';
import KYCPages from './kyc_pages';

// ==================== CONSTANTS ====================

/**
 * Available tab values for the KYC Configuration interface
 */
const TAB_VALUES = {
    PRODUCTS: 'kyc_products',
    PAGES: 'kyc_pages',
} as const;

const TAB_QUERY_PARAM = 'tab';

/**
 * Tab configuration with labels and component mappings
 */
const TAB_CONFIG = [
    {
        value: TAB_VALUES.PRODUCTS,
        label: 'KYC Products',
        component: KYCProducts,
    },
    {
        value: TAB_VALUES.PAGES,
        label: 'KYC Pages',
        component: KYCPages,
    },
] as const;

/**
 * Navigation routes for adding new items
 */
const ROUTES = {
    ADD_PRODUCT: '/kyc_configuration/add_product',
    ADD_PAGE: '/kyc_configuration/add_page',
} as const;

/**
 * Button configuration for action buttons
 */
const ACTION_BUTTONS = [
    {
        id: 'add-product',
        label: 'Add KYC Product',
        route: ROUTES.ADD_PRODUCT,
        ariaLabel: 'Add new KYC product',
    },
    {
        id: 'add-page',
        label: 'Add KYC Page',
        route: ROUTES.ADD_PAGE,
        ariaLabel: 'Add new KYC page',
    },
] as const;

// ==================== TYPES ====================

/**
 * Type for tab values
 */
type TabValue = typeof TAB_VALUES[keyof typeof TAB_VALUES];

const isTabValue = (value: string | null): value is TabValue =>
    value !== null && Object.values(TAB_VALUES).includes(value as TabValue);

// ==================== HELPER COMPONENTS ====================

/**
 * ActionButton Component
 * Reusable button component for actions with consistent styling
 */
interface ActionButtonProps {
    label: string;
    onClick: () => void;
    ariaLabel: string;
}

const ActionButton: React.FC<ActionButtonProps> = React.memo(({ label, onClick, ariaLabel }) => (
    <Button leftSection={<BsPlusCircle />} onClick={onClick} aria-label={ariaLabel}>
        {label}
    </Button>
));

ActionButton.displayName = 'ActionButton';

/**
 * TabPanel Component
 * Wrapper for tab content with lazy loading support
 */
interface TabPanelProps {
    value: string;
    children: React.ReactNode;
}

const TabPanel: React.FC<TabPanelProps> = React.memo(({ value, children }) => (
    <Tabs.Panel value={value}>{children}</Tabs.Panel>
));

TabPanel.displayName = 'TabPanel';

// ==================== CUSTOM HOOKS ====================

/**
 * Hook for managing tab state with type safety
 */
const useTabState = (defaultTab: TabValue = TAB_VALUES.PRODUCTS) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const requestedTab = searchParams.get(TAB_QUERY_PARAM);
    const [activeTab, setActiveTab] = useState<TabValue>(
        isTabValue(requestedTab) ? requestedTab : defaultTab
    );

    useEffect(() => {
        setActiveTab(isTabValue(requestedTab) ? requestedTab : defaultTab);
    }, [defaultTab, requestedTab]);

    const handleTabChange = useCallback((value: string | null) => {
        if (isTabValue(value)) {
            setActiveTab(value);
            setSearchParams((currentParams) => {
                const nextParams = new URLSearchParams(currentParams);
                nextParams.set(TAB_QUERY_PARAM, value);
                return nextParams;
            }, { replace: true });
        }
    }, [setSearchParams]);

    return { activeTab, handleTabChange };
};

/**
 * Hook for navigation actions
 */
const useNavigationActions = () => {
    const navigate = useNavigate();

    const navigateToRoute = useCallback(
        (route: string) => {
            navigate(route);
        },
        [navigate]
    );

    const handleAddProduct = useCallback(() => {
        navigateToRoute(ROUTES.ADD_PRODUCT);
    }, [navigateToRoute]);

    const handleAddPage = useCallback(() => {
        navigateToRoute(ROUTES.ADD_PAGE);
    }, [navigateToRoute]);

    return { handleAddProduct, handleAddPage, navigateToRoute };
};

// ==================== MAIN COMPONENT ====================

/**
 * KYCConfiguration Component
 * 
 * Main configuration page for managing KYC (Know Your Customer) products and pages.
 * Provides a tabbed interface to view and manage KYC products and pages with
 * quick access buttons to add new products or pages.
 * 
 * Features:
 * - Tabbed navigation between Products and Pages
 * - Quick action buttons for adding new items
 * - Lazy loading of tab content
 * - Responsive layout
 * - Accessible keyboard navigation
 * 
 * @component
 * @returns {JSX.Element} The KYC Configuration page with tabs and action buttons
 */
const KYCConfiguration: React.FC = () => {
    // ==================== Hooks ====================
    const { activeTab, handleTabChange } = useTabState();
    const { handleAddProduct, handleAddPage } = useNavigationActions();

    // ==================== Memoized Values ====================

    /**
     * Memoized action button handlers
     * Maps button configuration to actual handler functions
     */
    const actionHandlers = useMemo(
        () => ({
            [ROUTES.ADD_PRODUCT]: handleAddProduct,
            [ROUTES.ADD_PAGE]: handleAddPage,
        }),
        [handleAddProduct, handleAddPage]
    );

    // ==================== Render ====================

    return (
        <main style={{ padding: '1rem 0' }}>
            {/* Header section with title and action buttons */}
            <Group justify="space-between">
                <Title>KYC Configuration</Title>

                <Group>
                    {/* Render action buttons dynamically */}
                    {ACTION_BUTTONS.map((button) => (
                        <ActionButton
                            key={button.id}
                            label={button.label}
                            onClick={actionHandlers[button.route]}
                            ariaLabel={button.ariaLabel}
                        />
                    ))}
                </Group>
            </Group>

            <Space h="xl" />

            {/* Main content grid */}
            <Grid gap="md">
                <Grid.Col span={{ xs: 12 }}>
                    {/* Tabbed interface for switching between KYC Products and KYC Pages */}
                    <Tabs value={activeTab} onChange={handleTabChange}>
                        {/* Tab navigation */}
                        <Tabs.List>
                            {TAB_CONFIG.map((tab) => (
                                <Tabs.Tab key={tab.value} value={tab.value}>
                                    {tab.label}
                                </Tabs.Tab>
                            ))}
                        </Tabs.List>

                        <Space h="xl" />

                        {/* Tab panels with lazy-loaded content */}
                        {TAB_CONFIG.map((tab) => {
                            const TabComponent = tab.component;
                            return (
                                <TabPanel key={tab.value} value={tab.value}>
                                    {/* Only render active tab content for performance */}
                                    {activeTab === tab.value && <TabComponent />}
                                </TabPanel>
                            );
                        })}
                    </Tabs>
                </Grid.Col>
            </Grid>
        </main>
    );
};

export default KYCConfiguration;
